import React, { useState } from "react";
import { toast } from "sonner";
import { Images } from "lucide-react";
import { api } from "../lib/api";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useCurrency } from "../context/CurrencyContext";

export default function ImageOptionsDialog({ card, onSelected }) {
  const { format } = useCurrency();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [applyingIdx, setApplyingIdx] = useState(null);

  const load = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await api.get(`/cards/${card.id}/image-options`);
      setOptions(res.data.options || []);
    } catch {
      toast.error("Couldn't load image options. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const pick = async (opt, idx) => {
    setApplyingIdx(idx);
    try {
      const res = await api.post(`/cards/${card.id}/select-listing`, {
        title: opt.title,
        image_url: opt.image_url,
        price: opt.price,
        url: opt.url,
      });
      onSelected(res.data);
      setOpen(false);
      toast.success("Overlay image updated");
    } catch {
      toast.error("Could not apply that image. Please try again.");
    } finally {
      setApplyingIdx(null);
    }
  };

  return (
    <>
      <button
        data-testid="card-detail-browse-images-button"
        onClick={load}
        className="mt-1 flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors"
      >
        <Images className="w-3.5 h-3.5" /> Browse match images
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="image-options-dialog" className="bg-[#111113] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Choose the overlay image</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Official database artwork, photos from recent eBay sales, and this card's market
              matches. Tap the exact version of your card.
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <p className="text-sm text-zinc-500 py-8 text-center">Finding images...</p>
          ) : options.length === 0 ? (
            <p data-testid="image-options-empty" className="text-sm text-zinc-500 py-8 text-center">
              No image options found for this card — you can upload your own from the card page.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {options.map((opt, idx) => (
                <button
                  key={idx}
                  data-testid={`image-option-${idx}`}
                  onClick={() => pick(opt, idx)}
                  disabled={applyingIdx !== null}
                  className="text-left border border-white/10 rounded-lg overflow-hidden bg-black/30 hover:border-[#A78BFA]/60 transition-colors disabled:opacity-50"
                >
                  <div className="aspect-[3/4] bg-black/40">
                    <img src={pcImageUrl(opt.image_url, "240")} onError={pcImageFallback} alt={opt.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-1.5">
                    <p className="text-[10px] text-zinc-300 line-clamp-2 leading-tight">{opt.title}</p>
                    <p className={`text-[9px] uppercase tracking-widest mt-0.5 ${opt.source === "ebay_sold" ? "text-emerald-400" : "text-zinc-500"}`}>
                      {opt.source === "ebay_sold"
                        ? (opt.price != null ? `${format(opt.price)} · eBay sold` : "eBay sold")
                        : opt.source === "market" ? (opt.price != null ? `${format(opt.price)} · market` : "market") : "official"}
                      <span className="text-[#A78BFA] ml-1">{applyingIdx === idx ? "Applying..." : ""}</span>
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
